<template>
  <v-dialog
    v-model="visible"
    :max-width="time ? 638 : 397"
    persistent
  >
    <v-card v-if="visible" class="v-dialog--custom">
      <form class="form--custom" novalidate @submit.prevent="applyPeriod">
        <v-card-title class="headline pa-4">
          <span class="v-dialog--custom__title my-2">
            {{ $t(`${namespace}.choosePeriod`) }}
          </span>

          <v-btn
            class="v-dialog--custom__close mr-2"
            color="gray-6"
            fab
            flat
            @click="close"
          >
            <v-icon dark>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="v-dialog--custom__content px-4 pt-0">
          <transition name="fade">
            <div v-if="alert.visibility" class="form__alert">
              <v-icon color="error">warning</v-icon> <span>{{ alert.message }}</span>
            </div>
          </transition>

          <div class="period__container">
            <h2 class="period__subtitle">{{ $t(`${namespace}.from`) }}…</h2>
            <v-layout>
              <v-flex :class="time ? 'sm7' : 'sm12'">
                <div class="pr-2--desktop period__calendar__container">
                  <label>{{ $t(`${namespace}.fieldStartDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.startDate"
                    ref="startDate"
                    color="accent"
                    :name="$t(`${namespace}.fieldStartDateName`)"
                    :placeholder="$t(`${namespace}.fieldStartDatePlaceholder`)"
                    mask="##/##/####"
                    return-masked-value
                    append-icon="today"
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldStartDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldStartDateName`)"
                    required
                    flat
                    outline
                    solo
                    @click:append="openCalendar('startDateDialog')"
                  />
                  <v-dialog
                    v-model="startDateDialog"
                    :open-on-click="false"
                    :close-on-content-click="false"
                    lazy
                    full-width
                    width="290"
                  >
                    <v-date-picker
                      v-model="startDate"
                      :locale="currentLocale"
                      :max="today"
                      color="accent"
                      @change="(val) => closeCalendar('startDateDialog', 'startDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>

              <v-flex v-if="time" sm5>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldStartTimeLabel`) }}</label>
                  <v-text-field
                    v-model="startTime"
                    color="accent"
                    :name="$t(`${namespace}.fieldStartTimeName`)"
                    :placeholder="$t(`${namespace}.fieldStartTimePlaceholder`)"
                    mask="##:##"
                    return-masked-value
                    append-icon="query_builder"
                    v-validate="{ required: Boolean(endTime), date_format: 'HH:mm' }"
                    :error-messages="errors.collect($t(`${namespace}.fieldStartTimeName`))"
                    :data-vv-name="$t(`${namespace}.fieldStartTimeName`)"
                    required
                    flat
                    outline
                    solo
                  />
                </div>
              </v-flex>
            </v-layout>
          </div>

          <div class="period__container">
            <h2 class="period__subtitle">{{ $t(`${namespace}.to`) }}…</h2>
            <v-layout>
              <v-flex :class="time ? 'sm7' : 'sm12'">
                <div class="pr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEndDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.endDate"
                    ref="endDate"
                    color="accent"
                    :name="$t(`${namespace}.fieldEndDateName`)"
                    :placeholder="$t(`${namespace}.fieldEndDatePlaceholder`)"
                    mask="##/##/####"
                    return-masked-value
                    append-icon="today"
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldEndDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldEndDateName`)"
                    required
                    flat
                    outline
                    solo
                    @click:append="openCalendar('endDateDialog')"
                  />
                  <v-dialog
                    v-model="endDateDialog"
                    :open-on-click="false"
                    :close-on-content-click="false"
                    lazy
                    full-width
                    width="290"
                  >
                    <v-date-picker
                      v-model="endDate"
                      :locale="currentLocale"
                      :max="today"
                      color="accent"
                      @change="(val) => closeCalendar('endDateDialog', 'endDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>

              <v-flex v-if="time" sm5>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEndTimeLabel`) }}*</label>
                  <v-text-field
                    v-model="endTime"
                    color="accent"
                    :name="$t(`${namespace}.fieldEndTimeName`)"
                    :placeholder="$t(`${namespace}.fieldEndTimePlaceholder`)"
                    mask="##:##"
                    return-masked-value
                    append-icon="query_builder"
                    v-validate="{ required: Boolean(startTime), date_format: 'HH:mm' }"
                    :error-messages="errors.collect($t(`${namespace}.fieldEndTimeName`))"
                    :data-vv-name="$t(`${namespace}.fieldEndTimeName`)"
                    required
                    flat
                    outline
                    solo
                  />
                </div>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <a
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>

          <v-btn
            type="submit"
            round
            depressed
            color="accent"
          >
            {{ $t(`${namespace}.applyPeriod`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'

export default {
  name: 'PeriodDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    time: {
      type: Boolean,
      default: true
    },
    maxPeriod: {
      type: Number,
      default: 3
    }
  },
  computed: {
    today () {
      return new Date().toJSON().slice(0, 10)
    },

    currentLocale () {
      return this.$i18n.locale.split('_').join('-')
    }
  },
  data () {
    return {
      namespace: 'Components.MenuItemPeriod',

      // alert
      alert: {
        visibility: false,
        message: false
      },

      // dialogs
      startDateDialog: false,
      endDateDialog: false,

      // temp props
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      startDateTemp: null,
      endDateTemp: null,

      filters: {
        startDate: null,
        endDate: null
      }
    }
  },
  methods: {
    resetFields () {
      // alert
      this.alert = {
        visibility: false,
        message: false
      }

      // dialogs
      this.startDateDialog = false
      this.endDateDialog = false

      // temp props
      this.startDate = null
      this.startTime = null
      this.endDate = null
      this.endTime = null
      this.startDateTemp = null
      this.endDateTemp = null

      this.filters = {
        startDate: null,
        endDate: null
      }
    },

    alertError ({ visibility, message }) {
      this.alert = { visibility, message }
    },

    close ($event, val = 'currentMonth') {
      this.$emit('close', val)
    },

    convertDateToEnFormat (str) {
      return str.split('/').reverse().join('-')
    },

    convertDateToPtBrFormat (str) {
      return str.split('-').reverse().join('/')
    },

    openCalendar (dialogProp) {
      this[dialogProp] = true
    },

    closeCalendar (dialogProp, prop, val) {
      this[dialogProp] = false

      if (val) {
        this[prop] = val
        this.filters[prop] = this.convertDateToPtBrFormat(val)
        this.$refs[prop].focus()
      }
    },

    hideAlertError () {
      setTimeout(() => {
        this.alertError({ visibility: false, message: null })
      }, 4000)
    },

    validPeriod () {
      const today = moment().format('YYYY-MM-DD')
      const currentTime = moment().format('HH:mm')

      this.startDateTemp = this.startTime
        ? `${this.startDate}T${this.startTime}`
        : `${this.startDate}T00:00`

      this.endDateTemp = this.endTime
        ? `${this.endDate}T${this.endTime}`
        : `${this.endDate}T23:59`

      if (
        this.startDate === this.endDate &&
        this.startDate === today &&
        !this.startTime && !this.endTime
      ) {
        this.endDateTemp = `${this.endDate}T${currentTime}`
      }

      if (this.endDate === today && !this.endTime) {
        this.endDateTemp = `${this.endDate}T${currentTime}`
      }

      const todayMilliseconds = new Date().getTime()
      const startDateMilliseconds = new Date(this.startDateTemp).getTime()
      const endDateMilliseconds = new Date(this.endDateTemp).getTime()
      const monthsDiff = moment(this.endDateTemp).diff(moment(this.startDateTemp), 'months', true)

      if (endDateMilliseconds > todayMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.endDateLimitError`)
        })

        this.hideAlertError()

        this.$refs['endDate'].focus()
        return false
      }

      if (endDateMilliseconds <= startDateMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeError`)
        })

        this.hideAlertError()

        this.$refs['endDate'].focus()
        return false
      }

      if (monthsDiff > this.maxPeriod) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeLimitError`).replace('[MAX_PERIOD]', this.maxPeriod)
        })

        this.hideAlertError()

        return false
      }

      return true
    },

    async applyPeriod () {
      try {
        const isValid = await this.$validator.validateAll()

        if (!isValid) {
          this.alertError({
            visibility: true,
            message: this.$t(`${this.namespace}.fillInAdate`)
          })

          this.hideAlertError()
        }

        if (isValid && this.validPeriod()) {
          const startDate = this.startDateTemp
          const endDate = this.endDateTemp
          this.close(null, 'custom')
          this.$emit('applyPeriod', { startDate, endDate })
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    visible () {
      this.resetFields()
    },

    'filters.startDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.startDate = this.convertDateToEnFormat(val)
      }
    },

    'filters.endDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.endDate = this.convertDateToEnFormat(val)
      }
    }
  },
  created () {
    moment.locale(this.currentLocale)
    this.$validator.extend('isValidDate', {
      getMessage: field => this.$t(`${this.namespace}.invalidDate`),
      validate: value => {
        if (!value || value.length < 10) return false
        return moment(this.convertDateToEnFormat(value))._isValid
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.period__subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
}

.form__alert {
  background-color: #f2dede;
  border-bottom: 1px solid #D6D6D6;
  color: #a94442;
  font-size: 14px;
  margin-bottom: 24px;
  padding: 20px 18px;
  line-height: normal;
  text-align: center;

  .v-icon, span {
    vertical-align: middle;
    /* display: inline-block; */
  }

  .v-icon {
    margin-right: 5px;
  }
}

.v-dialog--custom__content {
  position: relative;
}

.form__alert {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1000;
}
</style>
